import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "./TabelaFiltroModal.scss";
import { FilterFichaFinanceiraDto } from "../../shared/dtos/FichaFinanceira.dto";
import { Label } from "../Label/styles";
import InputComponent from "../Input/input";
import Button from "../Button/button";
import SelectComponent from "../Select/select";
import { DatePickerInputContainer } from "../Input/styles";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { StatusImpressaoEnum } from "../../shared/enum/fichaFinanceira.enum";
import {
  BlueTitle,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../shared/styles/global.styles";
import { IoCloseSharp } from "react-icons/io5";

interface TabelaFiltroModalProps {
  onClose: () => void;
  onApplyFilters: (filters: FilterFichaFinanceiraDto) => void;
  onClearFilters: () => void;
  initialValues: FilterFichaFinanceiraDto;
  show: boolean;
}

const TabelaFiltroModal: React.FC<TabelaFiltroModalProps> = ({
  onClose,
  onApplyFilters,
  onClearFilters,
  initialValues,
  show,
}) => {
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([
    initialValues.dataVencimentoInicio
      ? new Date(initialValues.dataVencimentoInicio)
      : undefined,
    initialValues.dataVencimentoFim
      ? new Date(initialValues.dataVencimentoFim)
      : undefined,
  ]);

  const [paymentDateRange, setPaymentDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([
    initialValues.dataPagamentoInicio
      ? new Date(initialValues.dataPagamentoInicio)
      : undefined,
    initialValues.dataPagamentoFim
      ? new Date(initialValues.dataPagamentoFim)
      : undefined,
  ]);

  const [singlePaymentDate, setSinglePaymentDate] = useState<Date | undefined>(
    initialValues.dataPagamento
      ? new Date(initialValues.dataPagamento)
      : undefined
  );

  const [filters, setFilters] =
    useState<FilterFichaFinanceiraDto>(initialValues);

  const formatCurrency = (value: string) => {
    value = value.replace(/\D/g, ""); // Remove caracteres não numéricos
    value = (parseFloat(value) / 100).toFixed(2).replace(".", ","); // Formata como valor monetário
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, "."); // Adiciona separadores de milhar
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (["valorAjuste", "valorPago", "valorTarifa"].includes(name)) {
      formattedValue = formatCurrency(value);
    }
    setFilters({ ...filters, [name]: formattedValue });
  };

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setDateRange([start ?? undefined, end ?? undefined]);

    if (start) {
      setFilters({
        ...filters,
        dataVencimentoInicio: start.toISOString().split("T")[0] + "T00:00:00",
      });
    }

    if (end) {
      setFilters({
        ...filters,
        dataVencimentoFim: end.toISOString().split("T")[0] + "T23:59:59",
      });
    }
  };

  const handleSinglePaymentDateChange = (date: Date | null) => {
    setSinglePaymentDate(date || undefined);

    if (date) {
      setFilters({
        ...filters,
        dataPagamento: date.toISOString().split("T")[0] + "T00:00:00",
      });
    } else {
      const newFilters = { ...filters };
      delete newFilters.dataPagamento;
      setFilters(newFilters);
    }
  };

  const handlePaymentDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setPaymentDateRange([start ?? undefined, end ?? undefined]);

    if (start) {
      setFilters({
        ...filters,
        dataPagamentoInicio: start.toISOString().split("T")[0] + "T00:00:00",
      });
    }

    if (end) {
      setFilters({
        ...filters,
        dataPagamentoFim: end.toISOString().split("T")[0] + "T23:59:59",
      });
    }

  };

  // No handleSubmit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Converter o valor do intervalo para dataVencimentoInicio e dataVencimentoFim
    if (dateRange[0] && dateRange[1]) {
      const start = dateRange[0].toISOString().split("T")[0];
      const end = dateRange[1].toISOString().split("T")[0];

      // Atualiza o filtro com dataVencimentoInicio e dataVencimentoFim
      setFilters({
        ...filters,
        dataVencimentoInicio: start,
        dataVencimentoFim: end,
      });
    }
    // Converter o valor do intervalo de pagamento para dataPagamentoInicio e dataPagamentoFim
    if (paymentDateRange[0] && paymentDateRange[1]) {
      const start = paymentDateRange[0].toISOString().split("T")[0];
      const end = paymentDateRange[1].toISOString().split("T")[0];

      // Atualiza o filtro com dataPagamentoInicio e dataPagamentoFim
      setFilters({
        ...filters,
        dataPagamentoInicio: start,
        dataPagamentoFim: end,
      });
    }

    const appliedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== "")
    );
    onApplyFilters(appliedFilters);
    onClose(); // Fecha o modal
  };

  // Função para formatar a entrada manual de datas
  const handleDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Verificar se o valor contém hífen (separando intervalo de datas)
    const parts = value.split("-");

    if (parts.length > 1) {
      // Processar intervalo de datas (início - fim)
      let startDate = parts[0].trim();
      let endDate = parts[1].trim();

      // Formatar a primeira data
      startDate = formatDateInput(startDate);

      // Formatar a segunda data
      endDate = formatDateInput(endDate);

      // Recompor o valor com o formato correto
      e.target.value = `${startDate} - ${endDate}`;
    } else {
      // Processar uma única data
      const formattedDate = formatDateInput(value);

      // Verificar se a primeira data está completa (10 caracteres = dd/mm/aaaa)
      // Se estiver completa e não for uma operação de apagar (verificar se o valor atual é maior que o anterior)
      if (formattedDate.length === 10 && value.length >= formattedDate.length) {
        // Adicionar o hífen automaticamente
        e.target.value = `${formattedDate} - `;
      } else {
        e.target.value = formattedDate;
      }
    }
  };

  const handleOneDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Process single date format
    const formattedDate = formatDateInput(value);
    e.target.value = formattedDate;
  };

  // Função auxiliar para formatar uma data individual
  const formatDateInput = (value: string): string => {
    // Remover caracteres não numéricos
    let numbers = value.replace(/\D/g, "");

    // Limitar a 8 dígitos (ddmmyyyy)
    numbers = numbers.substring(0, 8);

    // Aplicar máscara dd/mm/yyyy
    let formattedValue = "";
    if (numbers.length > 0) {
      formattedValue = numbers.substring(0, Math.min(2, numbers.length));

      if (numbers.length > 2) {
        formattedValue +=
          "/" + numbers.substring(2, Math.min(4, numbers.length));
      }

      if (numbers.length > 4) {
        formattedValue +=
          "/" + numbers.substring(4, Math.min(8, numbers.length));
      }
    }

    return formattedValue;
  };

  const tipoCobrancaOptions = [
    { value: "Mensalidade", label: "Mensalidade" },
    { value: "Esporádica", label: "Esporádica" },
  ];
  const statusParcelaOptions = [
    { value: "Ativa", label: "Ativa" },
    { value: "Cancelada", label: "Cancelada" },
    { value: "Paga", label: "Paga" },
    // { value: "Vencida", label: "Vencida" },
  ];
  return (
    <ModalContainer show={show}>
      <ModalContent style={{ marginTop: "-2%" }}>
        <Button
          type="button"
          schemaButton="x-close"
          action={() => onClose()}
          icon={<IoCloseSharp size={30} />}
          position="absolute"
          top={10}
          right={10}
        />
        <ModalHeader>
          <BlueTitle>Filtrar por:</BlueTitle>
        </ModalHeader>
        <form className="filter-form" onSubmit={handleSubmit}>
          <ModalBody>
            <div className="full-column-filter">
              <Label className="filtro-label" htmlFor="tipoCobranca">
                Tipo cobrança
              </Label>
              <SelectComponent
                color="black"
                placeholder="Selecionar tipo de cobrança"
                id="tipoCobranca"
                name="tipoCobranca"
                value={filters.tipoCobranca ?? ""}
                onChange={handleChange}
                options={tipoCobrancaOptions}
              />
            </div>
            <div className="row-filter">
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="statusParcela">
                  Status parcela
                </Label>
                <SelectComponent
                  color="black"
                  placeholder="Selecionar status da parcela"
                  id="statusParcela"
                  name="statusParcela"
                  value={filters.statusParcela ?? ""}
                  onChange={handleChange}
                  options={statusParcelaOptions}
                />
              </div>
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="statusImpressao">
                  Status impressão
                </Label>
                <SelectComponent
                  color="black"
                  placeholder="Selecionar status da impressão"
                  id="statusImpressao"
                  name="statusImpressao"
                  value={filters.statusImpressao ?? ""}
                  onChange={handleChange}
                  options={[
                    {
                      value: StatusImpressaoEnum.A_IMPRIMIR,
                      label: "A imprimir",
                    },
                    { value: StatusImpressaoEnum.IMPRESSO, label: "Impresso" },
                    {
                      value: StatusImpressaoEnum.NAO_IMPRIMIR,
                      label: "Não imprimir",
                    },
                  ]}
                />
              </div>
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="statusRemessa">
                  Status remessa
                </Label>
                <SelectComponent
                  color="black"
                  placeholder="Selecionar status da remessa"
                  id="statusRemessa"
                  name="statusRemessa"
                  value={filters.statusRemessa ?? ""}
                  onChange={handleChange}
                  options={[
                    { value: "Enviar", label: "Enviar" },
                    { value: "Não enviar", label: "Não enviar" },
                    { value: "Enviada", label: "Enviada" },
                  ]}
                />
              </div>
            </div>
            <div className="row-filter">
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="dataVencimento">
                  Data de vencimento
                </Label>
                <DatePickerInputContainer
                  width={Number("100%")}
                  hasIconLeft={false}
                  hasIconRight={false}
                  error={false}
                  className={
                    dateRange[0] || dateRange[1] ? "has-date-content" : ""
                  }
                >
                  <DatePicker
                    selectsRange={true}
                    startDate={dateRange[0] as Date | undefined}
                    endDate={dateRange[1] as Date | undefined}
                    onChange={(dates) =>
                      handleDateRangeChange(dates as [Date | null, Date | null])
                    }
                    onChangeRaw={handleDateInputChange}
                    calendarStartDay={0}
                    className={`date-picker-input placeholder-custom ${
                      dateRange[0] || dateRange[1] ? "has-value" : "no-value"
                    }`}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000 - 00/00/0000"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    isClearable={false}
                    showYearDropdown
                    showMonthDropdown
                    scrollableMonthYearDropdown
                    wrapperClassName={
                      dateRange[0] || dateRange[1]
                        ? "date-wrapper-filled"
                        : "date-wrapper-empty"
                    }
                    locale="pt-BR"
                    autoComplete="off"
                    allowSameDay
                    onKeyDown={(e) => {
                      // Permite números, barras, backspace e teclas de navegação
                      if (
                        !/[0-9\/]/.test(e.key) &&
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight" &&
                        e.key !== "Tab"
                      ) {
                        e.preventDefault();
                      }

                      // Adiciona barras automaticamente
                      const input = e.currentTarget as HTMLInputElement;
                      if (/[0-9]/.test(e.key)) {
                        const value = input.value;
                        if (value.length === 2 || value.length === 5) {
                          input.value = value + "/";
                        }
                      }
                    }}
                  />
                </DatePickerInputContainer>
              </div>
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="dataPagamento">
                  Data de pagamento
                </Label>
                <DatePickerInputContainer
                  width={Number("100%")}
                  hasIconLeft={false}
                  hasIconRight={false}
                  error={false}
                  className={singlePaymentDate ? "has-date-content" : ""}
                >
                  <DatePicker
                    selected={singlePaymentDate}
                    onChange={handleSinglePaymentDateChange}
                    onChangeRaw={handleOneDateInputChange}
                    calendarStartDay={0}
                    className={`date-picker-input placeholder-custom ${
                      singlePaymentDate ? "has-value" : "no-value"
                    }`}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    isClearable={false}
                    showYearDropdown
                    showMonthDropdown
                    scrollableMonthYearDropdown
                    wrapperClassName={
                      singlePaymentDate
                        ? "date-wrapper-filled"
                        : "date-wrapper-empty"
                    }
                    locale="pt-BR"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      // Permite números, barras, backspace e teclas de navegação
                      if (
                        !/[0-9\/]/.test(e.key) &&
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight" &&
                        e.key !== "Tab"
                      ) {
                        e.preventDefault();
                      }

                      // Adiciona barras automaticamente
                      const input = e.currentTarget as HTMLInputElement;
                      if (/[0-9]/.test(e.key)) {
                        const value = input.value;
                        if (value.length === 2 || value.length === 5) {
                          input.value = value + "/";
                        }
                      }
                    }}
                  />
                </DatePickerInputContainer>
              </div>
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="intervalo">
                  {"Selecione o intervalo "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ zIndex: "9999" }} id="tooltip-cc">
                        Intervalo de data de pagamento
                      </Tooltip>
                    }
                  >
                    <i
                      className="fas fa-info-circle"
                      style={{
                        cursor: "help",
                        zIndex: "9999",
                        position: "relative",
                      }}
                    ></i>
                  </OverlayTrigger>
                </Label>
                <DatePickerInputContainer
                  width={Number("100%")}
                  hasIconLeft={false}
                  hasIconRight={false}
                  error={false}
                >
                  <DatePicker
                    selectsRange={true}
                    startDate={paymentDateRange[0] || undefined}
                    endDate={paymentDateRange[1] || undefined}
                    onChange={(e) => handlePaymentDateRangeChange(e)}
                    onChangeRaw={handleDateInputChange}
                    calendarStartDay={0}
                    className="date-picker-input"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="00/00/0000 - 00/00/0000"
                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    isClearable={false}
                    showYearDropdown
                    showMonthDropdown
                    scrollableMonthYearDropdown
                  />
                </DatePickerInputContainer>
              </div>
            </div>
            <div className="row-filter">
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="valorPago">
                  Valor pago
                </Label>
                <InputComponent
                  type="text"
                  name="valorPago"
                  id="valorParcela"
                  onChange={handleChange}
                  value={filters.valorPago?.toString() ?? ""}
                  placeholder="R$ 0,00"
                  color="black"
                />
              </div>
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="valorAjuste">
                  Valor ajuste
                </Label>
                <InputComponent
                  type="text"
                  name="valorAjuste"
                  id="valorParcela"
                  onChange={handleChange}
                  value={filters.valorAjuste?.toString() ?? ""}
                  placeholder="R$ 0,00"
                  color="black"
                />
              </div>
              <div className="column-filter">
                <Label className="filtro-label" htmlFor="valorTarifa">
                  Valor tarifa
                </Label>

                <InputComponent
                  type="text"
                  name="valorTarifa"
                  id="valorTarifa"
                  placeholder="R$ 0,00"
                  onChange={handleChange}
                  value={filters.valorTarifa?.toString() ?? ""}
                  color="black"
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button schemaButton="full-primary" type="submit">
              Filtrar
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalContainer>
  );
};
export default TabelaFiltroModal;
